// ** React Imports
import { useState, useEffect, useCallback } from 'react'

// ** Next Import
import { Link } from 'react-router-dom'

// ** MUI Imports
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import { DataGrid, } from '@mui/x-data-grid';
import Typography from '@mui/material/Typography'

// ** Custom Components Imports
import { client } from 'src/utils/axios-client'
import _ from 'lodash'
import PencilOutline from 'mdi-material-ui/PencilOutline'
import { IconButton } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete';
import useAwaitableComponent from '../../../../hooks/useAwaitableComponent'
import PageHeader from '../../../../materio/@core/components/page-header'
import ConfirmationDialog from '../../../common/ConfirmationDialog'
import NodeStore, { ChatListItem } from '../../stores/NodeStore'
import TableHeader from './TableHeader'
import useChats from '../../hooks/useChats'
import ChatDesigner from '../designer/ChatDesigner'


interface CellType {
    row: ChatListItem
}

const ChatList = () => {
    const [value, setValue] = useState<string>('')
    const [editOpen, setEditOpen] = useState<boolean>(false)
    const { chats, loadChats, loadChat, newChat, deleteChat } = useChats();
    const [isFunction, setIsFunction] = useState(false);
    const [selectedItem, setSelectedItem] = useState(undefined as undefined | ChatListItem);
    const [status, execute, resolve, reject, reset] = useAwaitableComponent();
    const showModal = status !== "idle";

    useEffect(() => {
        loadChats(chats.pageNumber, chats.pageSize, value, isFunction);
    }, [value, chats, isFunction])


    //useEffect(() => {
    //    onNewItem();
    //    onRowClick({
    //        id: '4F05A4CE-CDEE-453C-852B-E6FB76939B4F'
    //    } as ChatListItem)
    //},[])

    const handleConfirmDelete = async (row: ChatListItem) => {
        try {
            const result = await execute();
            await deleteChat(row.id);
            loadChats(1, chats.pageSize, value, isFunction);
        } catch (err) {

        } finally {
            reset();
        }
    };

    const columns = [
        {
            flex: 0.5,
            field: 'name',
            headerName: 'Name',
            renderCell: ({ row }: CellType) => {
                const { id, name } = row

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                            <Typography
                                noWrap
                                component='a'
                                variant='body2'
                                sx={{ fontWeight: 600, color: 'text.primary', textDecoration: 'none' }}
                            >
                                {name}
                            </Typography>
                        </Box>
                    </Box>
                )
            }
        },
        {
            flex: 0.2,
            field: 'published',
            headerName: 'Published',
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                            <Typography
                                noWrap
                                component='a'
                                variant='body2'
                                sx={{ fontWeight: 600, color: 'text.primary', textDecoration: 'none' }}
                            >
                                {row.published ? 'YES' : 'NO'}
                            </Typography>
                        </Box>
                    </Box>
                )
            }
        },
        {
            flex: 0.2,
            field: '',
            maxWidth: 150,
            headerName: 'Actions',
            renderCell: ({ row }: CellType) => {
                return (
                    <>
                        <IconButton onClick={() => handleConfirmDelete(row)} title="Delete">
                            <DeleteIcon fontSize='small' />
                        </IconButton>


                        <IconButton onClick={() => onRowClick(row)} title="Edit">
                            <PencilOutline fontSize='small' />
                        </IconButton>
                    </>
                )
            }
        }
    ]

    const handleFilter = useCallback((val: string) => {
        setValue(val)
    }, [])

    //const toggleEditAccountDrawer = async () => {
    //    if (editOpen) {
    //        await loadRecords(1, pageSize);
    //    }
    //    setEditOpen(!editOpen)
    //}

    const onRowClick = async (row: ChatListItem) => {
        await loadChat(row.id);

        setSelectedItem(row);
        setEditOpen(true);
    };

    const onNewItem = () => {
        var newItem = newChat();

        setSelectedItem(newItem);
        setEditOpen(true);
    }

    const refreshData = async () => {
        setSelectedItem(undefined);
        setEditOpen(true);
        loadChats(chats.pageNumber, chats.pageSize, value, isFunction);
    }

    if (selectedItem) return <ChatDesigner chatId={selectedItem.id} key={selectedItem.id} onClose={refreshData} open={selectedItem !== undefined} />

    return (
        <Grid container spacing={6} display='flex' height='100%' alignContent='flex-start'>
            <Grid item xs={12}>
                <PageHeader
                    title={<Typography variant='h5'>Chats</Typography>}
                    subtitle={
                        <Typography variant='body2'>
                            Create and manage chats here.
                        </Typography>
                    }
                />
            </Grid>
            <Grid item xs={12}>
                <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <TableHeader value={value} handleFilter={handleFilter} toggle={onNewItem} isFunction={isFunction} onIsFunctionChange={(value) => setIsFunction(value)} />
                    <DataGrid
                        autoHeight
                        pagination
                        rows={chats.items}
                        columns={columns}
                        paginationMode='server'
                        rowCount={chats.totalCount}
                        pageSize={chats.pageSize}
                        disableSelectionOnClick
                        rowsPerPageOptions={[10, 25, 50]}
                        onPageSizeChange={(newPageSize: number) => NodeStore.update((s, o) => {
                            const temp = structuredClone(o.chats);
                            temp.pageSize = newPageSize;
                            s.chats = temp;
                        })}
                        onPageChange={(newPage) => loadChats(newPage + 1, chats.pageSize, value, isFunction)}
                    />
                </Card>
            </Grid>

            <ConfirmationDialog open={showModal} onConfirm={resolve} onCancel={reject} title="Delete?" message="Are you sure you want to delete the chat?" confirmButtonText="Delete" cancelButtonText="Cancel" />
        </Grid>
    )
}

export { ChatList }
export default ChatList
